/**
 * Data access for `submitReferral`. Maps the mutation's Success variant — including a partial
 * send, where some recipients failed but real mail still went out — into a shape
 * `ReferAFriendModal` can render directly. Throws `SubmitReferralError` only for the
 * Failure variant, where the physician's name couldn't be resolved and nothing was attempted.
 */
import { gqlRequest } from '@mdvip/shared/lib/graphql-client';
import {
  SubmitReferralDocument,
  type SubmitReferralFailureCode,
  type SubmitReferralInput,
} from '@/graphql/generated/graphql';

// Named `Outcome`, not `Result` — the generated `SubmitReferralResult` is the GraphQL union
// (`SubmitReferralFailure | SubmitReferralSuccess`); this is the mapped Success shape only.
export interface SubmitReferralOutcome {
  sentCount: number;
  // Typed to the generated failure-code enum rather than narrowed to 'SEND_FAILED' — today it's
  // the only code the backend puts here, but this stays honest if that enum ever grows.
  failed: { email: string; code: SubmitReferralFailureCode }[];
  confirmationSent: boolean;
}

/**
 * Thrown only when `submitReferral` resolves to its Failure variant. A partial send (some
 * recipients failed) is still a Success at the GraphQL level and is returned, not thrown — see
 * `SubmitReferralOutcome.failed`.
 */
export class SubmitReferralError extends Error {
  constructor(public readonly code: SubmitReferralFailureCode) {
    super(code);
    this.name = 'SubmitReferralError';
  }
}

export async function requestSubmitReferral(input: SubmitReferralInput): Promise<SubmitReferralOutcome> {
  const data = await gqlRequest(SubmitReferralDocument, { input });
  const result = data.submitReferral;

  if (result.__typename === 'SubmitReferralFailure') {
    throw new SubmitReferralError(result.code);
  }

  return {
    sentCount: result.sentCount,
    failed: result.failed.map(({ email, code }) => ({ email, code })),
    confirmationSent: result.confirmationSent,
  };
}
